import React, { createContext, useContext, useEffect, useState } from "react";
import { BRAND } from "@/config";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { STRINGS } from "@/i18n";
import client from "@/lib/apiClient";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const { lang } = useLang();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vc_cart") || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("vc_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, displayName) => {
    setItems((prev) => {
      const found = prev.find((i) => i.id === product.id);
      if (found) return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { id: product.id, name: product.name, name_en: product.name_en, price: product.price, image_url: product.image_url, qty: 1 }];
    });
    const t = STRINGS[lang].whatsapp;
    toast.success(`${displayName || product.name} ${t.added}`);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) => {
    if (qty <= 0) return removeItem(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };
  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);

  const itemName = (i) => (lang === "en" && i.name_en ? i.name_en : i.name);

  const buildWhatsAppMsg = (customer, extraNote) => {
    const t = STRINGS[lang].whatsapp;
    let msg = `${t.orderGreeting}\n\n`;
    items.forEach((i) => {
      msg += `• ${i.qty}x ${itemName(i)} — $${(i.price * i.qty).toFixed(2)}\n`;
    });
    msg += `\n*${t.orderTotal}: $${total.toFixed(2)}*`;
    msg += `\n\n${t.shipHeader}\n${t.shipName}: ${customer.name}\n${t.shipPhone}: ${customer.phone}\n${t.shipAddress}: ${customer.address}`;
    if (extraNote) msg += `\n\n${extraNote}`;
    msg += `\n\n${t.orderClose}`;
    return `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  const checkoutWhatsApp = (customer) => {
    if (items.length === 0) return;
    window.open(buildWhatsAppMsg(customer), "_blank");
  };

  const checkoutStripe = async (customer) => {
    if (items.length === 0) return;
    const st = STRINGS[lang].cart;
    try {
      const { data } = await client.post("/payments/checkout", {
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        customer,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(st.orderError);
      }
    } catch (e) {
      toast.error(st.orderError);
    }
  };

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, count, total, open, setOpen, checkoutWhatsApp, checkoutStripe, itemName }}>
      {children}
    </CartContext.Provider>
  );
}
